import type { ArchiveSet } from './vfs';
import { detectLanguage } from './discs';
import { Reader } from './bytes';

/**
 * Localized string tables (`string.tbl`, `expansionstring.tbl`, `patchstring.tbl`):
 * a header, an index array, then a hash table of {key offset, value offset} entries.
 * Strings are latin-1; D2 color codes (`ÿc` + digit) are left in place for the text renderer.
 */
export function decodeStringTable(data: Uint8Array): Map<string, string> {
  const r = new Reader(data);
  r.skip(2); // crc
  const numElements = r.u16();
  const hashSize = r.u32();
  r.skip(1 + 4 + 4 + 4); // version, data start, max tries, file size
  r.skip(numElements * 2);
  const out = new Map<string, string>();
  const str = (off: number) => {
    let s = '';
    for (let i = off; i < data.length && data[i] !== 0; i++) s += String.fromCharCode(data[i]);
    return s;
  };
  for (let i = 0; i < hashSize; i++) {
    const used = r.u8();
    r.skip(2 + 4); // index, hash value
    const keyOff = r.u32();
    const valOff = r.u32();
    r.skip(2); // length
    if (!used) continue;
    const key = str(keyOff);
    if (!out.has(key)) out.set(key, str(valOff));
  }
  return out;
}

/** Layers string tables: later tables override earlier ones (string < expansion < patch). */
export class StringTable {
  private readonly map = new Map<string, string>();
  constructor(tables: Map<string, string>[]) {
    for (const t of tables) for (const [k, v] of t) this.map.set(k, v);
  }
  get(key: string): string {
    return this.map.get(key) ?? key;
  }
  has(key: string): boolean {
    return this.map.has(key);
  }
  get size(): number {
    return this.map.size;
  }
}

/**
 * Loads the layered string tables (string < expansionstring < patchstring) of `lang` — or, when
 * that language is absent or not given, of the language the player's files carry
 * (`detectLanguage`: a German LoD disc has `data\local\lng\deu`).
 */
export async function loadStrings(vfs: Pick<ArchiveSet, 'has' | 'tryRead'>, lang?: string | null): Promise<StringTable> {
  const l = lang && vfs.has(`data\\local\\lng\\${lang}\\string.tbl`) ? lang : (detectLanguage(vfs) ?? 'eng');
  const tables: Map<string, string>[] = [];
  for (const f of ['string', 'expansionstring', 'patchstring']) {
    const d = await vfs.tryRead(`data\\local\\lng\\${l}\\${f}.tbl`);
    if (d) tables.push(decodeStringTable(d));
  }
  return new StringTable(tables);
}
